import React, {
  useContext,
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo,
} from 'react';
import { debounce } from 'lodash';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/core';

import { LoadingIndicatorForDocument } from '../';
import { DOCUMENT_STATES, IFRAME_RESIZE_MESSAGE } from 'constants';
import {
  BookAppearanceValuesContext,
  DocumentModifiersContext,
  NetworkStatusValuesContext,
  OfflineValuesContext,
  MobileAppValuesContext,
  ReaderSettingsValuesContext,
  UserSessionValuesContext,
  DocumentValuesContext,
  UserSessionModifiersContext,
  BookLocationModifiersContext,
  BookLocationValuesContext,
  UserContentItemValuesContext,
  UserContentItemModifiersContext,
} from 'contexts';
import { DocumentScrollEvent } from '../../events';
import { useDimensions } from 'hooks';
import {
  cfiLogic,
  documentUtilities,
  getCfiForWordOffset,
  locationLogic,
  orderUserContentByCfi,
  resolveLunrSearchResults,
} from 'utilities';
import {
  findInlineUserContent,
  shineSpotlight,
} from 'utilities/documentUtilities';
import { HIGHLIGHT_OVERLAY_RECTANGLE_CLASS, STATUS_CODES } from 'constants';
import { BookDocumentInformation, BookLocation } from 'types';

const useStyles = makeStyles((theme) => {
  return {
    root: {},
    iframe: {
      border: 'none',
      width: '100%',
      verticalAlign: 'middle',
    },
    hide: {
      visibility: 'hidden',
    },
    screenReaderOnly: {
      border: 0,
      clip: 'rect(0, 0, 0, 0)',
      height: '1px',
      overflow: 'hidden',
      padding: 0,
      position: 'absolute !important',
      width: '1px',
    },
    '@media print': {
      iframe: {
        display: 'none',
      },
    },
  };
});

/**
 * This is the component for displaying epub (and converted pdf -> epub) content as xhtml in an iframe within our reader.
 */
const Document = ({
  bookLocation,
  className,
  highlightToFocus,
  isActive,
  isFixedWidth,
  onContextMenu,
  onCopiedTextClick,
  onDocumentClick,
  onDocumentCopy,
  onDocumentLinkClick,
  onDocumentMouseDown,
  onDocumentMouseUp,
  onDocumentRenderUserContent,
  onDocumentScroll,
  onDocumentSelectionChange,
  onDocumentTouchEnd,
  onDocumentTouchStart,
  onHighlightClick,
  onNoteHighlightClick,
  resetHighlightToFocus,
  setAnnounceLocationChangeMessage,
  userContentCopiedTexts,
  userContentHighlights,
  userContentNotes,
  userContentSelectedHighlight,
  userContentSelectedNote,
  ...props
}) => {
  const { user } = useContext(UserSessionValuesContext);
  const { setIsSessionExpired } = useContext(UserSessionModifiersContext);
  const { markersDownloaded, checkIsChapterDownloaded } =
    useContext(OfflineValuesContext);

  const {
    setCfi,
    setFrame: documentModifiersSetFrame,
    setPercentageScrolled,
    setState: documentModifiersSetState,
    setIsReadyToDisplay: documentModifiersSetIsReadyToDisplay,
  } = useContext(DocumentModifiersContext);
  const documentValuesContext = useContext(DocumentValuesContext);
  const { getRangeForCfi, getSnippetForCfi } = documentValuesContext;
  const bookAppearanceValues = useContext(BookAppearanceValuesContext);
  const readerSettingsValues = useContext(ReaderSettingsValuesContext);
  const { isOffline } = useContext(NetworkStatusValuesContext);
  const { isMobileApp, isMobileAppBookDownloaded } = useContext(
    MobileAppValuesContext,
  );
  const { goToLocation, setShouldFocusFrame } = useContext(
    BookLocationModifiersContext,
  );
  const { shouldFocusFrame, rawSpotlightData } = useContext(
    BookLocationValuesContext,
  );
  const { setRawSpotlightData } = useContext(BookLocationModifiersContext);
  const { resetUserContentItemToFocus } = useContext(
    UserContentItemModifiersContext,
  );
  const { userContentItemToFocus } = useContext(UserContentItemValuesContext);

  const frameRef = useRef();
  const [frame, setFrame] = useState(); // Make the frame available as a useState variable

  useEffect(() => {
    setFrame(frameRef.current);
  }, []);

  const {
    dimensions: containerDimensions,
    dimensionsUpdating: containerDimensionsUpdating,
  } = useDimensions(frame?.parentElement?.parentElement);

  const [isContentPositionStable, setIsContentPositionStable] = useState(false);
  const [state, setState] = useState(DOCUMENT_STATES.NEW);
  const [publisherContentDimensions, setPublisherContentDimensions] = useState({
    width: 0,
    height: 0,
  });
  const [waterMarks, setWaterMarks] = useState([]);

  /**
   * The "scroll event fingerprint" is for the functionality of locking the scroll position
   * to the current CFI even if the window or frame resizes.
   *
   * However, if the user scrolls -- but not enough to change the CFI -- it shouldn't force them
   * back to the exact place where the CFI starts.
   *
   * This fingerprint includes the following values:
   * - document index and CFI, to ensure that the Document should jump to the CFI when a bookLocation with a new CFI
   *   is passed down into the document
   * - container and content dimensions, to ensure that if any sizing changes occur it should re-jump to the CFI
   */
  const previousScrollEventFingerprintRef = useRef();
  const [orderedUserContentHighlights, setOrderedUserContentHighlights] =
    useState([]);
  const [orderedUserContentNotes, setOrderedUserContentNotes] = useState([]);
  const [orderedUserContentCopiedTexts, setOrderedUserContentCopiedTexts] =
    useState([]);

  const renderedHighlightsRef = useRef(new Set());
  const renderedNotesRef = useRef(new Set());
  const renderedUnsavedAnnotationRef = useRef();

  const inlineUserContentElementCache = useRef({});

  const [resolvedSpotlightData, setResolvedSpotlightData] = useState();

  const classes = useStyles();

  useEffect(
    function focusFrame() {
      if (!isActive || state !== DOCUMENT_STATES.FINISHED) {
        return;
      }

      if (!shouldFocusFrame) {
        return;
      }

      frameRef.current?.focus();
      setShouldFocusFrame(false);
    },
    [isActive, setShouldFocusFrame, shouldFocusFrame, state],
  );

  const handleSpotlightBlur = useCallback(
    (element, hasPresentationRole) => {
      element.removeAttribute('tabindex');
      hasPresentationRole && element.setAttribute('role', 'presentation');
      setAnnounceLocationChangeMessage(null);
    },
    [setAnnounceLocationChangeMessage],
  );

  // set list of highlights in the document ordered by cfi
  useEffect(() => {
    setOrderedUserContentHighlights((prevHighlights) => {
      return orderUserContentByCfi(
        frameRef.current,
        bookLocation?.document,
        userContentHighlights,
      );
    });
  }, [userContentHighlights, bookLocation?.document]);

  // set list of notes in the document ordered by cfi
  useEffect(() => {
    setOrderedUserContentNotes((prevNotes) => {
      return orderUserContentByCfi(
        frameRef.current,
        bookLocation?.document,
        userContentNotes,
      );
    });
  }, [userContentNotes, bookLocation?.document]);

  // set list of copied texts in the document ordered by cfi
  useEffect(() => {
    setOrderedUserContentCopiedTexts((prevCopiedTexts) => {
      return orderUserContentByCfi(
        frameRef.current,
        bookLocation?.document,
        userContentCopiedTexts,
      );
    });
  }, [userContentCopiedTexts, bookLocation?.document]);

  // Sync frame values with DocumentProvider
  useEffect(() => {
    documentModifiersSetFrame(frame);
  }, [documentModifiersSetFrame, frame]);

  // Sync state values with DocumentProvider
  useEffect(() => {
    documentModifiersSetState(state);
  }, [documentModifiersSetState, state]);

  // Set markers in current document
  const markersInCurrentDocument = useMemo(
    () => bookLocation?.document?.markers,
    [bookLocation?.document],
  );

  const focusOnHighlight = useCallback(() => {
    if (!highlightToFocus) {
      return;
    }

    const inlinehighlight =
      inlineUserContentElementCache.current[highlightToFocus.uuid];

    if (!inlinehighlight) {
      return;
    }

    inlinehighlight.buttonElement.focus();

    resetHighlightToFocus();
  }, [highlightToFocus, inlineUserContentElementCache, resetHighlightToFocus]);

  const renderingAnnotations = useRef(false);

  // Render inline highlights for userContent Highlights from backend(persisted/saved)
  const renderAnnotations = useCallback(() => {
    const frameDocument = frameRef.current?.contentDocument;
    // A few cases where we should make no attempt to manage annotation rendering.
    if (!isActive || state !== DOCUMENT_STATES.FINISHED || !frameDocument) {
      return;
    }

    renderingAnnotations.current = true;

    const selectedAnnotation =
      userContentSelectedHighlight || userContentSelectedNote;

    if (selectedAnnotation) {
      // There is an annotation selected.

      // Clear native selection before rendering css selection.
      frameDocument?.getSelection()?.empty();

      // Render css selection for the unsaved annotation.
      documentUtilities.insertInlineUserContent(selectedAnnotation, {
        frameDocument,
        getRangeForCfi,
        getSnippetForCfi,
      });

      renderedUnsavedAnnotationRef.current = selectedAnnotation;
    } else if (renderedUnsavedAnnotationRef.current) {
      // There was an unsaved annotation selected before, but no longer.

      // Clear unsaved annotation rendering.
      documentUtilities.removeInlineUserContent(
        findInlineUserContent(
          frameDocument,
          renderedUnsavedAnnotationRef.current.uuid,
        ),
      );

      inlineUserContentElementCache.current[
        renderedUnsavedAnnotationRef.current.uuid
      ] = null;

      renderedUnsavedAnnotationRef.current = null;
    }

    // Do not render notes not created by the user.
    const notesCreatedByUser = orderedUserContentNotes.filter((note) =>
      user.created(note),
    );

    // Do not render highlights not created by the user.
    const highlightsCreatedByUser = orderedUserContentHighlights.filter(
      (highlight) => user.created(highlight),
    );

    // Throwaway value for copied texts, to please the linter.
    const copiedTextsCreatedByUser = orderedUserContentCopiedTexts.filter(
      (copiedText) => user.created(copiedText),
    );
    copiedTextsCreatedByUser.forEach(() => {});

    // In offline mode, some annotations should not render when the reader is offline and the related book content is not visible.
    const markerCheck = (annotation) =>
      isMobileAppBookDownloaded ||
      !annotation.marker ||
      checkIsChapterDownloaded(annotation.marker);

    const highlightsToRender = new Set(
      isOffline
        ? highlightsCreatedByUser.filter(markerCheck)
        : highlightsCreatedByUser,
    );
    const highlightUUIDsToRender = new Set(
      [...highlightsToRender].map((highlight) => highlight.uuid),
    );

    const notesToRender = new Set(
      isOffline ? notesCreatedByUser.filter(markerCheck) : notesCreatedByUser,
    );
    const noteUUIDsToRender = new Set(
      [...notesToRender].map((note) => note.uuid),
    );

    // Previously rendered annotations that should no longer be rendered should be removed.
    const highlightsToRemove = [...renderedHighlightsRef.current].filter(
      (annotation) => !highlightUUIDsToRender.has(annotation.uuid),
    );

    const notesToRemove = [...renderedNotesRef.current].filter(
      (annotation) => !noteUUIDsToRender.has(annotation.uuid),
    );

    // Prevent re-rendering selected annotations using data that exists for existing annotations.
    // This allows for notes created from highlights, with the same UUID, to show real-time color updates in the document prior to saving.
    // In short, this makes the (often unsaved) selected annotation "win." Its rendering is handled in a separate useEffect.
    const selectedAnnotationUUIDs = [
      userContentSelectedHighlight?.uuid,
      userContentSelectedNote?.uuid,
    ];

    // Render all highlights that are not yet rendered.
    for (let highlight of highlightsToRender) {
      if (selectedAnnotationUUIDs.includes(highlight.uuid)) {
        continue;
      }

      inlineUserContentElementCache.current[highlight.uuid] =
        documentUtilities.insertInlineUserContent(
          highlight,
          { frameDocument, getRangeForCfi, getSnippetForCfi },
          highlight.isReadOnly ? null : () => onHighlightClick(highlight),
          inlineUserContentElementCache.current[highlight.uuid],
        );
    }

    // Render all notes that are not yet rendered.
    for (let note of notesToRender) {
      if (selectedAnnotationUUIDs.includes(note.uuid)) {
        continue;
      }

      inlineUserContentElementCache.current[note.uuid] =
        documentUtilities.insertInlineUserContent(
          note,
          { frameDocument, getRangeForCfi, getSnippetForCfi },
          note.isReadOnly ? null : () => onNoteHighlightClick(note),
          inlineUserContentElementCache.current[note.uuid],
        );
    }

    // Clear all annotations that should no longer be rendered.
    for (let annotation of [...highlightsToRemove, ...notesToRemove]) {
      documentUtilities.removeInlineUserContent(
        findInlineUserContent(frameDocument, annotation),
      );

      inlineUserContentElementCache.current[annotation.uuid] = null;
    }

    renderedHighlightsRef.current = highlightsToRender;
    renderedNotesRef.current = notesToRender;
    renderingAnnotations.current = false;

    focusOnHighlight();
    onDocumentRenderUserContent();
  }, [
    isActive, // document must be active to render annotations
    state, // document must have finished loading to render annotations
    getRangeForCfi, // utility function with cache that will only change when document changes
    getSnippetForCfi, // utility function with cache that will only change when document changes
    checkIsChapterDownloaded,
    isOffline,
    isMobileAppBookDownloaded,
    onHighlightClick,
    onNoteHighlightClick,
    user,
    orderedUserContentCopiedTexts, // TODO: use same approach to implement copied texts if we decide to show them again
    orderedUserContentHighlights,
    orderedUserContentNotes,
    onDocumentRenderUserContent,
    userContentSelectedHighlight,
    userContentSelectedNote,
    focusOnHighlight,
  ]);

  const renderAnnotationsDebounced = useMemo(
    () => debounce(renderAnnotations, 200),
    [renderAnnotations],
  );

  // Render SVG censor for content not available for offline, when offline
  const renderSvgCensor = useCallback(async () => {
    if (!frameRef.current) {
      return;
    }

    documentUtilities.removeSvgCovers(frameRef.current);

    // Don't need to add SVG censor if online or if whole book is downloaded,
    // and noTOC books downloaded (only 100% download available for noTOC).
    // In mobile app, when the book is downloaded for offline we don't add covers
    // (reader's markersDownloaded may be empty; content is served from native cache).
    const isWholeBookDownloaded = checkIsChapterDownloaded();

    if (
      !isOffline ||
      isWholeBookDownloaded ||
      (isMobileApp && isMobileAppBookDownloaded)
    ) {
      setWaterMarks([]);
      const iframeContainer = frameRef.current.parentElement;
      iframeContainer.style.overflow = 'visible';
      return;
    }

    // No markers Available for Offline, cover everything
    if (!markersDownloaded.size) {
      const iframeContainer = frameRef.current.parentElement;
      iframeContainer.style.overflow = 'hidden';

      documentUtilities.insertSvgBetweenNodes(
        frameRef.current,
        markersInCurrentDocument[0].index,
        frameRef.current.contentDocument.body,
      );
      return;
    }

    // List of markers/chapters in document not available
    const markersToCover = markersInCurrentDocument.filter(
      (marker) => !checkIsChapterDownloaded(marker),
    );

    // All markers in the document available for offline
    if (!markersToCover.length) {
      return;
    }

    // No marker in the document available for offline
    if (markersToCover.length === markersInCurrentDocument.length) {
      const iframeContainer = frameRef.current.parentElement;
      iframeContainer.style.overflow = 'hidden';

      documentUtilities.insertSvgBetweenNodes(
        frameRef.current,
        markersInCurrentDocument[0].index,
        frameRef.current.contentDocument.body,
      );

      // Markers which are not available is made visually inaccessible
    } else {
      for (let i = 0; i < markersInCurrentDocument.length; i++) {
        const marker = markersInCurrentDocument[i];

        if (!markersToCover.includes(marker)) {
          continue;
        }

        const markerElementId = marker.element;

        let startId, endId;

        if (!markerElementId || i === 0) {
          startId = { markerPosition: 'startOfDocument' };
        } else {
          startId = markerElementId;
        }

        let nextMarkerIndexInDocument = i + 1;

        while (
          nextMarkerIndexInDocument < markersInCurrentDocument.length &&
          !endId
        ) {
          const nextMarker =
            markersInCurrentDocument[nextMarkerIndexInDocument];

          if (nextMarker) {
            const nextMarkerElementId = nextMarker.element;
            if (nextMarkerElementId) {
              endId = nextMarkerElementId;
            }
          }

          nextMarkerIndexInDocument++;
        }

        if (!endId) {
          endId = { markerPosition: 'endOfDocument' };
        }

        documentUtilities.insertSvgBetweenNodes(
          frameRef.current,
          marker.index,
          startId,
          endId,
        );
      }
    }

    // Adding Error Message on top of Svg Covers
    setWaterMarks(
      documentUtilities.addWaterMarkToSvgCover(
        frameRef.current.contentDocument,
      ),
    );
  }, [
    checkIsChapterDownloaded,
    markersDownloaded,
    markersInCurrentDocument,
    isOffline,
    isMobileApp,
    isMobileAppBookDownloaded,
  ]);

  const renderSvgCensorDebounced = useMemo(
    () => debounce(renderSvgCensor, 200),
    [renderSvgCensor],
  );

  const updatePublisherContentDimensions = useCallback(() => {
    setPublisherContentDimensions(
      JSON.stringify(
        documentUtilities.syncFrameToContentDimensions(
          frameRef.current,
          bookAppearanceValues.fontSize,
          bookLocation.document,
        ),
      ),
    );
  }, [bookAppearanceValues.fontSize, bookLocation.document]);

  const makeScrollEventFingerprint = useCallback(
    (cfi, percentage) => {
      return JSON.stringify({
        documentIndex: bookLocation.document.index,
        cfi,
        percentage,
        containerDimensions,
        publisherContentDimensions,
      });
    },
    [
      bookLocation.document.index,
      containerDimensions,
      publisherContentDimensions,
    ],
  );

  const scrollHandler = useCallback(
    (event) => {
      if (containerDimensionsUpdating) {
        // Do not interpret the Document as changing locations while it is "scrolling" as it is reflowing.
        return;
      }

      const activeContainer = frameRef.current.parentElement;
      const percentage = documentUtilities.getPercentageScrolled(
        frameRef.current,
      );
      const cfi = locationLogic.getCfiForPixelOffset(
        frameRef.current,
        activeContainer.parentElement.scrollTop,
      );

      onDocumentScroll(event, percentage, cfi);

      setCfi(cfi);
      setPercentageScrolled(percentage);

      previousScrollEventFingerprintRef.current = !isFixedWidth
        ? makeScrollEventFingerprint(cfi)
        : makeScrollEventFingerprint(cfi, percentage);
    },
    [
      onDocumentScroll,
      makeScrollEventFingerprint,
      containerDimensionsUpdating,
      isFixedWidth,
      setCfi,
      setPercentageScrolled,
    ],
  );

  const messageHandler = useCallback(
    ({ data }) => {
      if (data !== IFRAME_RESIZE_MESSAGE) {
        return;
      }

      updatePublisherContentDimensions();
    },
    [updatePublisherContentDimensions],
  );

  const resizeHandler = useCallback(() => {
    updatePublisherContentDimensions();
  }, [updatePublisherContentDimensions]);

  const preventDefault = useCallback((event) => {
    event.preventDefault();
    return false;
  }, []);

  // Handle the source URL changing. This happens when the document changes.
  useEffect(() => {
    setState(DOCUMENT_STATES.HAS_SOURCE);
    const sourceURL = bookLocation.document.sourceURL;

    // Loads the document into the iframe via location.replace and advances state to HTML_LOADED
    // when content is ready. Used on successful fetch and, when mobile+offline, when fetch fails
    // so the iframe still navigates and the WebView can serve the spine from cache.
    const loadFrameFromSource = () => {
      if (!frameRef.current?.contentDocument) {
        return;
      }
      const myBodyIsReady = documentUtilities.waitForDocumentContent(
        () => frameRef.current,
      );
      frameRef.current.contentDocument?.location?.replace(sourceURL);
      myBodyIsReady
        .then(() => {
          setState(DOCUMENT_STATES.HTML_LOADED);
        })
        .catch((e) => {
          console.warn('Waiting for document content failed', sourceURL, e);
        });
    };

    // Fetch first so we have a chance to catch if the source returns an HTTP error.
    fetch(sourceURL)
      .then((r) => {
        if (r.status === STATUS_CODES.FORBIDDEN) {
          setIsSessionExpired(true);
          return;
        }
        loadFrameFromSource();
      })
      .catch((e) => {
        console.error('Document fetch failed', sourceURL, e);
        // When mobile+offline, still try loading the iframe so the WebView can serve from cache.
        // If the native cache also fails, loadFrameFromSource's waitForDocumentContent will reject
        // or never resolve; the .catch there only logs, so we don't throw. State stays HAS_SOURCE
        // and the user sees loading/blank—no crash.
        if (isMobileApp && isOffline) {
          loadFrameFromSource();
        }
      });
  }, [
    bookLocation.document.sourceURL,
    bookLocation.document.index,
    setIsSessionExpired,
    isMobileApp,
    isOffline,
  ]);

  // When the source loads, make alterations to the html
  useEffect(() => {
    if (!frameRef.current?.contentDocument) {
      return;
    }

    // Once the html is available, we can perform modifications to it, such as decryption.
    if (state === DOCUMENT_STATES.HTML_LOADED) {
      setState(DOCUMENT_STATES.HTML_ALTERING);
      documentUtilities
        .prepareDocumentHTML(frameRef.current, {
          shouldAddMathJax: bookLocation.document.hasMathML,
        })
        .then(() => {
          setState(DOCUMENT_STATES.HTML_ALTERED);
        });
    }
  }, [state, bookLocation.document.hasMathML]);

  // If the html has been decrypted, wait for subresources before considering it loaded
  useEffect(() => {
    if (!frameRef.current?.contentDocument) {
      return;
    }

    if (state === DOCUMENT_STATES.HTML_ALTERED) {
      const frameDocument = frameRef.current.contentDocument;

      // Only wait for subresources if this is the active document
      if (isActive) {
        documentUtilities.waitForSubresources(frameDocument).then(() => {
          setState(DOCUMENT_STATES.LOADED);
        });
      } else {
        // For inactive/preload documents, skip subresource wait
        setState(DOCUMENT_STATES.LOADED);
      }
    }
  }, [isActive, state, bookLocation.document.index]);

  const [aTags, setATags] = useState([]);
  // Ensure that links within the content have the latest link handler listener
  useEffect(() => {
    if (!frameRef.current?.contentDocument) {
      return;
    }

    if (state >= DOCUMENT_STATES.HTML_LOADED) {
      const aTags = Array.from(
        frameRef.current.contentDocument.getElementsByTagName('a'),
      );

      setATags(aTags);

      aTags.forEach((aTag) =>
        aTag.addEventListener('click', onDocumentLinkClick),
      );

      return () => {
        aTags.forEach((aTag) =>
          aTag.removeEventListener('click', onDocumentLinkClick),
        );
      };
    }
  }, [state, onDocumentLinkClick]);

  // Add svg overlay on top of links with action to go to the href of the link
  // z-index of this is greater than that of highlights so that link action take priority over highlight action if, highlight is created over link.
  // not using z-index on link directly as that has possibility to mess up publisher's intended content flow.
  const renderSvgOverLinks = useCallback(() => {
    if (!frameRef.current?.contentDocument) {
      return;
    }

    documentUtilities.removeSvgOverElement(frameRef.current);
    // TEMPORARY: do not insert clickable svgs if there are more than 750 aTags on the page
    // this causes performance issues and the reader can crash
    aTags.length <= 750 &&
      aTags.forEach((aTag, idx) => {
        documentUtilities.insertSvgOverElement(
          frameRef.current,
          aTag,
          idx,
          onDocumentLinkClick,
        );
      });
  }, [aTags, onDocumentLinkClick]);

  const renderSvgOverLinksDebounced = useMemo(
    () => debounce(renderSvgOverLinks, 200),
    [renderSvgOverLinks],
  );

  // Apply frame configuration (styling) and adjust height in response to configuration or state changes.
  useEffect(() => {
    if (!frameRef.current?.contentDocument) {
      return;
    }

    if (isActive && state >= DOCUMENT_STATES.LOADED) {
      documentUtilities
        .applyConfiguration(
          frameRef.current,
          bookAppearanceValues,
          readerSettingsValues,
          bookLocation.document.fixedWidth,
          bookLocation.document.fixedHeight,
        )
        .then(() => {
          // Set state to FINISHED when styling configuration has been applied at least once.
          setState(DOCUMENT_STATES.FINISHED);
        });

      updatePublisherContentDimensions();
    }
  }, [
    isActive,
    state,
    bookLocation.document.fixedWidth,
    bookLocation.document.fixedHeight,
    isFixedWidth,
    bookAppearanceValues,
    readerSettingsValues,
    updatePublisherContentDimensions,
  ]);

  // If the document is active, and the html is loaded, scroll to match the most up-to-date location.
  // Do this when the location changes or if the html is changed (loaded html, altered html, loaded subresources)
  // If the user starts scrolling, this will no longer apply until they navigate in a different way again.
  useEffect(() => {
    if (!frameRef.current?.contentDocument) {
      return;
    }

    if (!bookLocation) {
      return;
    }

    if (!isActive || state !== DOCUMENT_STATES.FINISHED) {
      return;
    }

    // Very important that it first has the chance to calculate the intended iframe height
    // if it doesn't do this first, then it may try to scroll to the location percentage before the content is even scrollable
    updatePublisherContentDimensions();

    // Now that the frame has a height, go to the correct scroll position for the current location.
    bookLocation.resolve(frameRef.current); // ensure a cfi is available

    const newScrollEventFingerprint = !isFixedWidth
      ? makeScrollEventFingerprint(bookLocation.cfi)
      : makeScrollEventFingerprint(
          bookLocation.cfi,
          bookLocation?.percentageOfDocument,
        );

    if (
      newScrollEventFingerprint === previousScrollEventFingerprintRef.current ||
      bookLocation.event instanceof DocumentScrollEvent
    ) {
      setIsContentPositionStable(true);
      return;
    }

    // scrollToCfi cannot be implemented for pdf and fixed-width books
    // coz they have one cfi for whole document so using scrollToPercentage instead
    if (!isFixedWidth) {
      setIsContentPositionStable(false);

      // when at start or end of the document,
      // specific element at the cfi may not be at the start or end of document so using scrollToPercentage instead
      if (
        bookLocation.percentageOfDocument === 1 ||
        bookLocation.percentageOfDocument === 0
      ) {
        documentUtilities.scrollToPercentage(
          frameRef.current,
          bookLocation.percentageOfDocument,
        );
      } else {
        try {
          // Scrolling to CFI is to be preferred to scrolling to elements because the CFIs attached to locations are the most accurate
          documentUtilities.scrollToCfi(frameRef.current, bookLocation.cfi);
        } catch {
          // If scrollToCfi fails, we can try to use scrollToElement as a fallback.
          // This will always happen when navigating to a RedShelf-autogenerated pagebreak location.
          // RedShelf-autogenerated pagebreak locations have CFIs that are ignored by the CFI parser.
          // scrollToElement needs the HTML to be loaded and have valid body to find a bookLocation.element
          if (bookLocation.element) {
            documentUtilities.scrollToElement(
              frameRef.current,
              bookLocation.element,
            );
          }
        }
      }
    } else {
      documentUtilities.scrollToPercentage(
        frameRef.current,
        bookLocation.percentageOfDocument,
      );
    }
    previousScrollEventFingerprintRef.current = newScrollEventFingerprint;
    setIsContentPositionStable(true);
  }, [
    bookLocation,
    isActive,
    isFixedWidth,
    makeScrollEventFingerprint, // includes 'containerDimensions' and 'publisherContentDimensions' dependency
    state,
    updatePublisherContentDimensions,
  ]);

  useEffect(
    function resolveSpotlightData() {
      (async () => {
        const frameDocument = frameRef.current?.contentDocument;

        if (
          !isActive ||
          state !== DOCUMENT_STATES.FINISHED ||
          !frameDocument ||
          !isContentPositionStable ||
          !rawSpotlightData ||
          bookLocation?.documentIndex !==
            rawSpotlightData.searchResult.bookLocation.document.index
        ) {
          return;
        }

        // For elasticsearch, use the spotlight data as-is.
        if (rawSpotlightData.isFromElasticSearchResult) {
          setRawSpotlightData(null);
          setResolvedSpotlightData({
            ...rawSpotlightData,
            bookLocation: rawSpotlightData.searchResult.bookLocation,
            cfi: rawSpotlightData.searchResult.bookLocation.cfi,
          });

          return;
        }

        // Set up BookDocumentInformation
        const bookDocumentInformation = new BookDocumentInformation(
          frameDocument,
          rawSpotlightData.searchResult.bookLocation.document,
        );
        bookDocumentInformation.buildWordObjects(
          BookDocumentInformation.MODES.NO_RANGE_RECTANGLES,
        );

        // For lunr, re-run the search against the current state of the document.
        let resolvedSpotlightData;
        const newResults = await resolveLunrSearchResults({
          book: rawSpotlightData.searchResult.bookLocation.book,
          getDocumentInformation: () => bookDocumentInformation,
          lunrSearchResults: rawSpotlightData.lunrSearchResults,
          queryString: rawSpotlightData.searchResult.queryString,
          limitResolutionToDocumentIndex:
            rawSpotlightData.searchResult.bookLocation.document.index,
          frameDocument,
        });

        const candidateResults = [];
        for (const newResult of newResults) {
          if (newResult.snippet === rawSpotlightData.searchResult.snippet) {
            candidateResults.push(newResult);
          }
        }

        let bestResult;
        let bestResultDistance;
        for (const candidateResult of candidateResults) {
          // Find the result with the closest indexRelativeToDocument.
          const distance = Math.abs(
            candidateResult.indexRelativeToDocument -
              rawSpotlightData.searchResult.indexRelativeToDocument,
          );
          if (!bestResult || distance < bestResultDistance) {
            bestResult = candidateResult;
            bestResultDistance = distance;
          }
        }

        if (!bestResult) {
          console.warn(
            'Failed to find a matching search result after re-running the search.',
          );

          setRawSpotlightData(null);
          setResolvedSpotlightData(null);

          return;
        }

        const cfi = getCfiForWordOffset(
          bookDocumentInformation,
          bestResult.bookLocation.wordOffset,
        );

        resolvedSpotlightData = {
          ...rawSpotlightData,
          bookLocation: bestResult.bookLocation,
          cfi,
        };

        setRawSpotlightData(null);
        setResolvedSpotlightData(resolvedSpotlightData);
      })();
    },
    [
      bookLocation,
      isActive,
      isContentPositionStable,
      rawSpotlightData,
      setRawSpotlightData,
      state,
    ],
  );

  useEffect(
    function focusSpotlight() {
      const frameDocument = frameRef.current?.contentDocument;

      if (
        !isActive ||
        state !== DOCUMENT_STATES.FINISHED ||
        !frameDocument ||
        !isContentPositionStable ||
        !resolvedSpotlightData ||
        bookLocation?.documentIndex !==
          resolvedSpotlightData.searchResult.bookLocation.document.index
      ) {
        return;
      }

      setResolvedSpotlightData(null);

      let element;
      try {
        element = cfiLogic.getElementForCfi(
          frameDocument,
          resolvedSpotlightData.cfi,
        );
      } catch (e) {
        console.warn(e);
        return;
      }

      // pdfs have role="presentation" on spans that
      // prevent the screen reader from focusing them
      const hasPresentationRole =
        element.getAttribute('role') === 'presentation';
      hasPresentationRole && element.removeAttribute('role');

      const handleSpotlightBlurCaller = () =>
        handleSpotlightBlur(element, hasPresentationRole);

      goToLocation(
        new BookLocation({
          document: resolvedSpotlightData.bookLocation.document,
          cfi: resolvedSpotlightData.cfi,
        }),
        { shouldReplaceHistoryEntry: true },
      ).then(() => {
        shineSpotlight(frameDocument, resolvedSpotlightData.cfi);

        element.setAttribute('tabindex', 0);

        element.focus();
        element.addEventListener('blur', handleSpotlightBlurCaller);

        setTimeout(() => {
          setAnnounceLocationChangeMessage(
            <span>
              Navigated to{' '}
              <span className="noTranslate">
                {resolvedSpotlightData.bookLocation.readableLocation}
              </span>
              .
            </span>,
          );
        }, 2000);
      });

      return () =>
        element.removeEventListener('blur', handleSpotlightBlurCaller);
    },
    [
      handleSpotlightBlur,
      setAnnounceLocationChangeMessage,
      resolvedSpotlightData,
      bookLocation,
      goToLocation,
      isActive,
      isContentPositionStable,
      state,
    ],
  );

  // check if document is ready to render
  // On mobile we relax the conditions so content can show even when isContentPositionStable is
  // false and during dimension updates. This is intentional: offline, static assets (rs-cpl-base.css/js)
  // often fail to load, so dimensions/scroll never stabilize; requiring them would keep the iframe
  // hidden and cause a white screen. We prefer showing possibly briefly unstable content over
  // hiding it indefinitely. On web we still require FINISHED + isContentPositionStable.
  const readyToDisplay =
    state >=
      (isMobileApp ? DOCUMENT_STATES.LOADED : DOCUMENT_STATES.FINISHED) &&
    isContentPositionStable &&
    (!containerDimensionsUpdating || isFixedWidth || isMobileApp);

  // When the content resizes, clear highlight overlays so they can be re-rendered.
  // This must run before the useEffect that triggers rendering of annotations.
  useEffect(
    function clearRenderedAnnotationOverlays() {
      if (!frameRef.current?.contentDocument) {
        return;
      }

      frameRef.current.contentDocument
        ?.querySelectorAll(`.${HIGHLIGHT_OVERLAY_RECTANGLE_CLASS}`)
        .forEach((e) => e.remove());
    },
    [publisherContentDimensions, bookAppearanceValues.fontSize],
  );

  // Insert user content when the document is ready for it.
  // Update it if it changes, or if the document content changes or resizes.
  useEffect(
    function runRenderAnnotationsDebounced() {
      if (isActive && readyToDisplay && state === DOCUMENT_STATES.FINISHED) {
        renderAnnotationsDebounced();
      }
    },
    [
      isActive,
      readyToDisplay,
      state,
      renderAnnotationsDebounced,
      publisherContentDimensions,
      bookAppearanceValues.fontSize,
    ],
  );

  // Insert SVG on top of links
  useEffect(() => {
    if (isActive && state === DOCUMENT_STATES.FINISHED) {
      renderSvgOverLinksDebounced();
    }
  }, [
    isActive,
    state,
    renderSvgOverLinksDebounced,
    publisherContentDimensions,
    bookAppearanceValues.fontSize,
  ]);

  // Insert SVG overlays when the document is ready for it.
  // Update if the document content resizes or downloaded markers changes or network status changes.
  useEffect(() => {
    if (isActive && state === DOCUMENT_STATES.FINISHED) {
      renderSvgCensorDebounced();
    }
  }, [
    isActive,
    state,
    renderSvgCensorDebounced,
    publisherContentDimensions,
    bookAppearanceValues.fontSize,
  ]);

  // Attach listeners if the document is fully rendered.
  useEffect(() => {
    if (!frameRef.current?.contentDocument) {
      return;
    }

    const frameDocument = frameRef.current.contentDocument;
    const frameParent = frameRef.current.parentElement.parentElement;

    if (isActive && state >= DOCUMENT_STATES.FINISHED) {
      frameParent.addEventListener('scroll', scrollHandler); // "root" div of DocumentFraming scrolls keeping scrollbar attached to right of screen
      frameDocument.addEventListener('touchstart', onDocumentTouchStart);
      frameDocument.addEventListener('touchend', onDocumentTouchEnd);
      frameDocument.addEventListener('click', onDocumentClick);
      frameDocument.addEventListener('mousedown', onDocumentMouseDown);
      frameDocument.addEventListener('mouseup', onDocumentMouseUp);
      frameDocument.addEventListener(
        'selectionchange',
        onDocumentSelectionChange,
      );
      frameDocument.addEventListener('copy', onDocumentCopy);
      frameDocument.addEventListener('contextmenu', onContextMenu);
      frameDocument.addEventListener('dragstart', preventDefault);

      // Watch for height changes in the publisher content
      window.addEventListener('message', messageHandler);

      // Watch for height changes in the eReader window
      window.addEventListener('resize', resizeHandler);

      return () => {
        frameParent.removeEventListener('scroll', scrollHandler);
        frameDocument.removeEventListener('touchstart', onDocumentTouchStart);
        frameDocument.removeEventListener('touchend', onDocumentTouchEnd);
        frameDocument.removeEventListener('click', onDocumentClick);
        frameDocument.removeEventListener('mousedown', onDocumentMouseDown);
        frameDocument.removeEventListener('mouseup', onDocumentMouseUp);
        frameDocument.removeEventListener(
          'selectionchange',
          onDocumentSelectionChange,
        );
        frameDocument.removeEventListener('copy', onDocumentCopy);
        frameDocument.removeEventListener('contextmenu', onContextMenu);
        frameDocument.removeEventListener('dragstart', preventDefault);
        window.removeEventListener('message', messageHandler);
        window.removeEventListener('resize', resizeHandler);
      };
    }
  }, [
    isActive,
    state,
    onDocumentLinkClick,
    onDocumentScroll,
    onDocumentTouchStart,
    onDocumentTouchEnd,
    onDocumentClick,
    onDocumentMouseDown,
    onDocumentMouseUp,
    onContextMenu,
    onDocumentSelectionChange,
    onDocumentCopy,
    preventDefault,
    scrollHandler,
    messageHandler,
    resizeHandler,
  ]);

  useEffect(() => {
    documentModifiersSetIsReadyToDisplay(readyToDisplay);
  }, [readyToDisplay, documentModifiersSetIsReadyToDisplay]);

  const focusInlineHighlight = useCallback(() => {
    const inlineHighlight =
      inlineUserContentElementCache.current[userContentItemToFocus.uuid];

    if (!inlineHighlight) {
      return;
    }

    inlineHighlight.buttonElement.focus();
    resetUserContentItemToFocus();
  }, [userContentItemToFocus, resetUserContentItemToFocus]);

  const debounceFocusInlineHighlight = useMemo(
    () => debounce(focusInlineHighlight, 200),
    [focusInlineHighlight],
  );

  useEffect(
    function focusHighlightSelectedFromLTMenu() {
      if (
        readyToDisplay &&
        userContentItemToFocus &&
        bookLocation?.document?.index ===
          userContentItemToFocus.document?.index &&
        !renderingAnnotations.current
      ) {
        debounceFocusInlineHighlight();
      }
    },
    [
      readyToDisplay,
      userContentItemToFocus,
      bookLocation?.document?.index,
      renderingAnnotations,
      debounceFocusInlineHighlight,
    ],
  );

  const shouldHideFrame = !isMobileApp && !readyToDisplay;

  return (
    <>
      <div className={readyToDisplay ? classes.hide : null}>
        <LoadingIndicatorForDocument />
      </div>

      {props.id ? (
        <div
          id="mainContent"
          className={classes.screenReaderOnly}
          tabIndex={-1}
        >
          Start of main content.
        </div>
      ) : null}
      <iframe
        data-document-index={bookLocation.document.index}
        ref={frameRef}
        src="about:blank"
        title="Book Content"
        className={clsx(
          className,
          classes.iframe,
          shouldHideFrame && classes.hide,
          'noTranslate',
        )}
        {...props}
      />
      {waterMarks}
    </>
  );
};

Document.propTypes = {
  bookLocation: PropTypes.object.isRequired,
  isActive: PropTypes.bool, // set to false to use this Document instance to preload content in the background
  className: PropTypes.string,
  userContentSelectedHighlight: PropTypes.object,
  userContentHighlights: PropTypes.array,
  userContentCopiedTexts: PropTypes.array,
  onDocumentLinkClick: PropTypes.func, // For handling all <a> tag clicks in book content
  onDocumentRenderUserContent: PropTypes.func,
  onDocumentScroll: PropTypes.func,
  onDocumentTouchStart: PropTypes.func,
  onDocumentTouchEnd: PropTypes.func,
  onDocumentClick: PropTypes.func,
  onDocumentMouseDown: PropTypes.func,
  onDocumentMouseUp: PropTypes.func,
  onContextMenu: PropTypes.func,
  onDocumentSelectionChange: PropTypes.func,
  onDocumentCopy: PropTypes.func,
  onHighlightClick: PropTypes.func,
  onCopiedTextClick: PropTypes.func,
  resetHighlightToFocus: PropTypes.func,
  setAnnounceLocationChangeMessage: PropTypes.func,
};

Document.defaultProps = {
  isActive: true,
  className: null,
  userContentSelectedHighlight: null,
  userContentHighlights: [],
  userContentCopiedTexts: [],
  onDocumentLinkClick: (event) => {},
  onDocumentRenderUserContent: () => {},
  onDocumentScroll: (event, cfi) => {},
  onDocumentTouchStart: (event) => {},
  onDocumentTouchEnd: (event) => {},
  onDocumentClick: (event) => {},
  onDocumentMouseDown: (event) => {},
  onDocumentMouseUp: (event) => {},
  onDocumentSelectionChange: (event) => {},
  onContextMenu: (event) => {},
  onDocumentCopy: (event) => {},
  onHighlightClick: (event) => {},
  onCopiedTextClick: (event) => {},
  resetHighlightToFocus: () => {},
  setAnnounceLocationChangeMessage: () => {},
};

export default Document;
